import {
  AHIcon,
  AHTeamIconCircle,
  AHText,
  Box,
  resolveAHTextStyle,
  useBoxStyle,
  useLayoutContext,
  useTheme,
} from "arkh";
import React, { memo, useCallback, useEffect, useRef } from "react";
import { Platform, TextInput, View } from "react-native";
import Color from "color";
import { ItemButton, ItemButtonTitle } from "./ItemButton";
import { FormattedMessage, useIntl } from "react-intl";
import { Team } from "../../../types/Team";
import * as Haptics from "expo-haptics";
import { TeamIcon } from "./RoutineEditChecklistSection/__generated__/RoutineEditChecklistSectionIssueTeamQuery.graphql";
import { TopicAndDisableableIndicator } from "./TopicAndDisableableIndicator";

type Props = {
  itemId: string;
  selected: boolean;
  index: number;
  numberOfTasks: number;
  sectionId: string;
  title: string | null;
  assignedIssueTeam: Team | null;

  onDeleteSection?: (sectionId: string) => void;
  onSetSelectedItem(itemId: string, index: number): void;
  onEditIssueTeam(sectionId: string): void;
  onSetTitle(sectionId: string, title: string): void;
  onOrgEditSectionSettings(sectionId: string): void;
  isOrg: boolean;
  orgCanBeDisabled: boolean;
  orgTopic: {
    name: string;
  } | null;
  autoFocus?: boolean;
};

const Component = (props: Props) => {
  const theme = useTheme();
  const layout = useLayoutContext();
  const inputRef = useRef<TextInput>(null);
  const intl = useIntl();
  const {
    onSetTitle,
    onDeleteSection,
    sectionId,
    itemId,
    index,
    onSetSelectedItem,
  } = props;
  const selected = props.selected;

  const assignedIssueTeamLabel = intl.formatMessage({
    defaultMessage: "Assigned team:",
    description:
      "RoutineEditChecklist Section Header Issue responsible team label",
  });
  const onChangeText = useCallback(
    (title: string) => {
      onSetTitle(sectionId, title);
    },
    [onSetTitle, sectionId]
  );
  const onPressDelete = useCallback(() => {
    onDeleteSection?.(sectionId);
  }, [onDeleteSection, sectionId]);

  const boxStyle = useBoxStyle({
    cornerRadiusTopLeft: "large",
    cornerRadiusTopRight: "large",
    cornerRadiusBottomLeft: props.numberOfTasks === 0 ? "large" : "none",
    cornerRadiusBottomRight: props.numberOfTasks === 0 ? "large" : "none",
  });

  const titleSmallTextStyle = resolveAHTextStyle(
    "title-small",
    1,
    theme,
    layout
  );

  useEffect(() => {
    if (props.autoFocus) {
      inputRef.current?.focus();
    }
  }, [props.autoFocus]);
  return (
    <Box
      style={{
        paddingBottom: props.numberOfTasks > 0 ? layout.radius.large : 0,
        marginBottom: props.numberOfTasks > 0 ? -layout.radius.large : 0,
        backgroundColor: selected
          ? Color(theme.primary).lighten(0.95).toString()
          : "transparent",
      }}
      cornerRadiusTopLeft="large"
      cornerRadiusTopRight="large"
      cornerRadiusBottomLeft={props.numberOfTasks === 0 ? "large" : "none"}
      cornerRadiusBottomRight={props.numberOfTasks === 0 ? "large" : "none"}
    >
      <Box
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <TextInput
          ref={inputRef}
          style={[
            boxStyle,
            titleSmallTextStyle,
            {
              width: "100%",
              paddingHorizontal: layout.space.medium,
              paddingVertical: selected
                ? layout.space.medium
                : layout.space.small,
            },
          ]}
          autoCapitalize="sentences"
          value={props.title ?? ""}
          onChangeText={onChangeText}
          placeholder={intl.formatMessage({
            defaultMessage: "Section name",
            description:
              "RoutineEditChecklistSectionHeader section name input placeholder",
          })}
          placeholderTextColor={theme["text-washed"]}
          onFocus={() => {
            if (Platform.OS === "ios") {
              Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
            }
            onSetSelectedItem(itemId, index);
          }}
        />
        {props.isOrg ? (
          <TopicAndDisableableIndicator
            orgCanBeDisabled={props.orgCanBeDisabled}
            orgTopic={props.orgTopic}
          />
        ) : null}
      </Box>
      {!selected && props.assignedIssueTeam ? (
        <Box
          style={{ flexDirection: "row", alignItems: "center" }}
          spaceHorizontal="medium"
          spaceBottom="small"
        >
          <AHText>{`${assignedIssueTeamLabel} `}</AHText>
          <AHTeamIconCircle
            size="small"
            color={props.assignedIssueTeam.color}
            type={props.assignedIssueTeam.icon as TeamIcon}
            isPrivate={props.assignedIssueTeam.isPrivate}
          />
          <AHText spaceHorizontal="xsmall">
            {props.assignedIssueTeam.name}
          </AHText>
        </Box>
      ) : null}
      {selected ? (
        <Box
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          spaceBottom="medium"
        >
          {!props.isOrg ? (
            <ItemButton onPress={() => props.onEditIssueTeam(sectionId)}>
              {props.assignedIssueTeam ? (
                <>
                  <AHText>{`${assignedIssueTeamLabel} `}</AHText>
                  <AHTeamIconCircle
                    size="small"
                    color={props.assignedIssueTeam.color}
                    type={props.assignedIssueTeam.icon as TeamIcon}
                    isPrivate={props.assignedIssueTeam.isPrivate}
                  />
                  <AHText spaceHorizontal="xsmall">
                    {props.assignedIssueTeam.name}
                  </AHText>
                </>
              ) : (
                <ItemButtonTitle>
                  <FormattedMessage
                    defaultMessage="Assign team"
                    description="RoutineEditChecklistSectionHeader issue responsible team cta"
                  />
                </ItemButtonTitle>
              )}
            </ItemButton>
          ) : (
            <View style={{ flex: 1 }}>
              <ItemButton
                onPress={() => props.onOrgEditSectionSettings(sectionId)}
              >
                <AHIcon type="more-outlined" color="primary" />
              </ItemButton>
            </View>
          )}
          {selected && props.onDeleteSection ? (
            <ItemButton borderColor="negative" onPress={onPressDelete}>
              <AHIcon type="trash" color="negative" />
            </ItemButton>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export const RoutineEditChecklistSectionHeader = memo(Component);
